import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Pool, Team, PoolType } from '../../types/pool';

interface TeamSelection {
  teamId: string;
  seed: number;
  selected: boolean;
}

const SPORT_FOR_POOL: Record<string, string> = {
  [PoolType.NHL_PLAYOFF]: 'NHL',
  [PoolType.NBA_PLAYOFF]: 'NBA',
  [PoolType.NFL_PLAYOFF]: 'NFL',
};

const POSITION_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  NBA: [
    { value: 'G', label: 'Guard (G)' },
    { value: 'F', label: 'Forward (F)' },
    { value: 'C', label: 'Center (C)' },
  ],
  NHL: [
    { value: 'C', label: 'Center (C)' },
    { value: 'LW', label: 'Left Wing (LW)' },
    { value: 'RW', label: 'Right Wing (RW)' },
    { value: 'D', label: 'Defenseman (D)' },
    { value: 'G', label: 'Goaltender (G)' },
  ],
  NFL: [
    { value: 'QB', label: 'QB' },
    { value: 'RB', label: 'RB' },
    { value: 'WR', label: 'WR' },
    { value: 'TE', label: 'TE' },
    { value: 'K', label: 'K' },
    { value: 'DEF', label: 'DEF' },
  ],
};

export function AdminPlayoffSetup() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [pool, setPool] = useState<Pool | null>(null);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [selections, setSelections] = useState<Map<string, TeamSelection>>(new Map());
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importingPlayers, setImportingPlayers] = useState(false);
  const [savingPositions, setSavingPositions] = useState(false);
  const [result, setResult] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [step, setStep] = useState<'teams' | 'players'>('teams');
  const [needsPosition, setNeedsPosition] = useState<Array<{
    externalId: string;
    firstName: string;
    lastName: string;
    teamName: string;
    teamId: string;
    jerseyNumber: string;
    position: string; // user-assigned
  }>>([]);

  useEffect(() => {
    if (poolId) loadData();
  }, [poolId]);

  async function loadData() {
    try {
      setLoading(true);
      const poolData = await api.getPool(poolId!);
      setPool(poolData);

      const sport = SPORT_FOR_POOL[poolData.type];
      if (sport) {
        const token = await getAccessTokenSilently();
        try {
          const { teams } = await api.getLeagueTeams(sport, token);
          setAllTeams(teams);

          // Pre-select teams already in the pool
          const poolTeams = await api.getPoolTeams(poolId!);
          const newSelections = new Map<string, TeamSelection>();
          teams.forEach(t => {
            const existing = poolTeams?.find((pt: any) => pt.teamId === t.id || pt.team?.id === t.id);
            newSelections.set(t.id, {
              teamId: t.id,
              seed: existing?.seed || 0,
              selected: !!existing,
            });
          });
          setSelections(newSelections);
        } catch {
          // Teams not seeded yet - that's okay
          setAllTeams([]);
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSeedTeams() {
    if (!pool) return;
    const sport = SPORT_FOR_POOL[pool.type];
    if (!sport) return;

    try {
      setSeeding(true);
      setResult(null);
      const token = await getAccessTokenSilently();
      const res = await api.seedLeagueTeams(sport, token);
      setResult({ message: `Seeded ${(res as any).created || 0} new teams, updated ${(res as any).updated || 0}`, type: 'success' });
      // Reload teams
      const { teams } = await api.getLeagueTeams(sport, token);
      setAllTeams(teams);
      const newSelections = new Map<string, TeamSelection>();
      teams.forEach((t: Team) => {
        newSelections.set(t.id, { teamId: t.id, seed: 0, selected: false });
      });
      setSelections(newSelections);
    } catch (err) {
      setResult({ message: err instanceof Error ? err.message : 'Failed to seed teams', type: 'error' });
    } finally {
      setSeeding(false);
    }
  }

  function toggleTeam(teamId: string) {
    setSelections(prev => {
      const next = new Map(prev);
      const current = next.get(teamId);
      if (current) {
        next.set(teamId, { ...current, selected: !current.selected });
      }
      return next;
    });
  }

  function setSeed(teamId: string, seed: number) {
    setSelections(prev => {
      const next = new Map(prev);
      const current = next.get(teamId);
      if (current) {
        next.set(teamId, { ...current, seed });
      }
      return next;
    });
  }

  const selectedTeams = Array.from(selections.values()).filter(s => s.selected);
  const selectedCount = selectedTeams.length;

  async function handleSaveTeams() {
    if (!poolId || selectedCount === 0) return;

    try {
      setSaving(true);
      setResult(null);
      const token = await getAccessTokenSilently();
      const teams = selectedTeams.map(s => ({
        teamId: s.teamId,
        seed: s.seed,
      }));
      await api.selectPlayoffTeams(poolId, teams, token);
      setResult({ message: `${selectedCount} playoff teams saved successfully!`, type: 'success' });
      setStep('players');
    } catch (err) {
      setResult({ message: err instanceof Error ? err.message : 'Failed to save teams', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleImportPlayers() {
    if (!poolId) return;

    try {
      setImportingPlayers(true);
      setResult(null);
      setNeedsPosition([]);
      const token = await getAccessTokenSilently();
      const res = await api.autoImportRosters(poolId, token);

      const npCount = res.needsPosition?.length || 0;
      setResult({
        message: `Imported ${res.imported || 0} players, updated ${res.updated || 0} (${res.teamsProcessed || 0} teams)${npCount ? `. ${npCount} players need position assignment.` : ''}${res.errors?.length ? ` Errors: ${res.errors.join(', ')}` : ''}`,
        type: 'success',
      });

      // Populate the position assignment list
      if (res.needsPosition?.length > 0) {
        setNeedsPosition(res.needsPosition.map((p: any) => ({ ...p, position: '' })));
      }
    } catch (err) {
      setResult({ message: err instanceof Error ? err.message : 'Failed to import players', type: 'error' });
    } finally {
      setImportingPlayers(false);
    }
  }

  async function handleSavePositions() {
    if (!poolId || needsPosition.length === 0) return;

    const unassigned = needsPosition.filter(p => !p.position);
    if (unassigned.length > 0) {
      setResult({ message: `${unassigned.length} player(s) still need a position assigned.`, type: 'error' });
      return;
    }

    try {
      setSavingPositions(true);
      setResult(null);
      const token = await getAccessTokenSilently();
      const res = await api.assignPlayerPositions(poolId, needsPosition, token);
      setResult({
        message: `Saved ${res.created || 0} players with assigned positions.${res.errors?.length ? ` Errors: ${res.errors.join(', ')}` : ''}`,
        type: 'success',
      });
      setNeedsPosition([]);
    } catch (err) {
      setResult({ message: err instanceof Error ? err.message : 'Failed to save positions', type: 'error' });
    } finally {
      setSavingPositions(false);
    }
  }

  // Group teams by conference/division
  const teamsByConference = allTeams.reduce((acc, team) => {
    const conf = team.conference || 'Other';
    if (!acc[conf]) acc[conf] = [];
    acc[conf].push(team);
    return acc;
  }, {} as Record<string, Team[]>);

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin" className="text-sm text-blue-600 hover:text-blue-700 mb-2 inline-block">
          &larr; Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Playoff Team Setup</h1>
        {pool && (
          <p className="text-gray-600 mt-1">
            {pool.name} &bull; <span className="text-blue-600">{SPORT_FOR_POOL[pool.type]} Playoffs</span>
          </p>
        )}
      </div>

      {/* Result Banner */}
      {result && (
        <div className={`mb-6 p-4 rounded-lg border ${
          result.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <p className="text-sm font-medium">{result.message}</p>
        </div>
      )}

      {/* Step Indicator */}
      <div className="flex items-center gap-4 mb-8">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
          step === 'teams' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
        }`}>
          <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs font-bold">1</span>
          Select Playoff Teams
        </div>
        <div className="h-px flex-1 bg-gray-300" />
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
          step === 'players' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'
        }`}>
          <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs font-bold">2</span>
          Import Players
        </div>
      </div>

      {/* No teams seeded yet */}
      {allTeams.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">
            No {SPORT_FOR_POOL[pool?.type || ''] || ''} teams found in the database
          </h3>
          <p className="text-sm text-yellow-700 mb-4">
            You need to seed the league teams first. This is a one-time setup that adds all {SPORT_FOR_POOL[pool?.type || ''] || ''} teams to the database.
          </p>
          <button
            onClick={handleSeedTeams}
            disabled={seeding}
            className="px-6 py-3 bg-yellow-600 text-white font-medium rounded-lg hover:bg-yellow-700 disabled:bg-gray-300 transition-colors"
          >
            {seeding ? 'Seeding Teams...' : `Seed All ${SPORT_FOR_POOL[pool?.type || ''] || ''} Teams`}
          </button>
        </div>
      )}

      {/* Team Selection Grid */}
      {allTeams.length > 0 && step === 'teams' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Selected: <span className={`font-bold ${selectedCount === 16 ? 'text-green-600' : selectedCount > 0 ? 'text-blue-600' : 'text-gray-400'}`}>{selectedCount}/16</span> teams
            </p>
            {selectedCount > 0 && selectedCount < 16 && (
              <p className="text-sm text-amber-600">You can save now and add remaining teams later</p>
            )}
          </div>

          {Object.entries(teamsByConference).sort().map(([conference, teams]) => (
            <div key={conference} className="bg-white rounded-lg shadow">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                <h3 className="font-semibold text-gray-800">{conference} Conference</h3>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {teams.map(team => {
                  const sel = selections.get(team.id);
                  const isSelected = sel?.selected || false;
                  return (
                    <div
                      key={team.id}
                      className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      onClick={() => {
                        toggleTeam(team.id);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-gray-900">{team.shortName}</p>
                          <p className="text-xs text-gray-500">{team.division}</p>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="mt-2" onClick={e => e.stopPropagation()}>
                          <label className="text-xs text-gray-600">Seed</label>
                          <input
                            type="number"
                            min={1}
                            max={16}
                            value={sel?.seed || ''}
                            onChange={e => setSeed(team.id, parseInt(e.target.value) || 0)}
                            className="w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="#"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            onClick={handleSaveTeams}
            disabled={saving || selectedCount === 0}
            className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : `Save ${selectedCount} Playoff Teams`}
          </button>
        </div>
      )}

      {/* Player Import Step */}
      {step === 'players' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Players</h3>
            <p className="text-sm text-gray-600 mb-4">
              You can auto-import all existing players from the selected teams, or manually import player rosters via JSON on the Import Data page.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleImportPlayers}
                disabled={importingPlayers}
                className="flex-1 px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors"
              >
                {importingPlayers ? 'Importing...' : 'Auto-Import Players from Teams'}
              </button>

              <Link
                to={`/admin/pools/${poolId}/import`}
                className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 text-center transition-colors"
              >
                Manual Import (JSON)
              </Link>
            </div>
          </div>

          {/* Position Assignment UI */}
          {needsPosition.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Assign Missing Positions</h3>
              <p className="text-sm text-gray-600 mb-4">
                {needsPosition.length} player{needsPosition.length !== 1 ? 's' : ''} returned from the API without a position. Assign each one below, then save.
              </p>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">#</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Player</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Team</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Position</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {needsPosition.map((player, idx) => {
                      const sport = pool ? SPORT_FOR_POOL[pool.type] || 'NBA' : 'NBA';
                      const posOptions = POSITION_OPTIONS[sport] || POSITION_OPTIONS.NBA;
                      return (
                        <tr key={player.externalId} className={!player.position ? 'bg-yellow-50' : ''}>
                          <td className="px-4 py-2 text-gray-500">{player.jerseyNumber || '—'}</td>
                          <td className="px-4 py-2 font-medium text-gray-900">
                            {player.firstName} {player.lastName}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{player.teamName}</td>
                          <td className="px-4 py-2">
                            <select
                              value={player.position}
                              onChange={(e) => {
                                const updated = [...needsPosition];
                                updated[idx] = { ...updated[idx], position: e.target.value };
                                setNeedsPosition(updated);
                              }}
                              className={`px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                !player.position ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                              }`}
                            >
                              <option value="">Select position...</option>
                              {posOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center gap-4">
                <button
                  onClick={handleSavePositions}
                  disabled={savingPositions}
                  className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors"
                >
                  {savingPositions ? 'Saving...' : `Save ${needsPosition.length} Player Positions`}
                </button>
                <span className="text-sm text-gray-500">
                  {needsPosition.filter(p => !p.position).length} still unassigned
                </span>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Next Steps</h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Import or auto-import players (above)</li>
              <li>Import the playoff schedule via the <Link to={`/admin/pools/${poolId}/import`} className="underline">Import Data</Link> page</li>
              <li>Set up scoring rules (the scoring JSON templates are in <code className="bg-blue-100 px-1 rounded">backend/data/</code>)</li>
              <li>Open the pool for entries!</li>
            </ol>
          </div>

          <button
            onClick={() => setStep('teams')}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            &larr; Back to team selection
          </button>
        </div>
      )}
    </div>
  );
}
